// services/leave.js — полагаем платен годишен отпуск (чл. 155–156а КТ,
// НРВПО чл. 42) и давност на отпуска (чл. 176 и 176а КТ). Чисти функции.
//
// Нормативна основа (сверена в актуалната редакция на 25.08.2026):
// - чл. 155, ал. 2 КТ: право на ползване при най-малко 4 месеца ПЪРВИ
//   трудов стаж; ал. 4: основен размер не по-малко от 20 работни дни.
// - чл. 156 КТ: допълнителен отпуск — мин. 5 раб. дни (ненормиран ден /
//   специфични условия); чл. 156а — по-големи размери по договор/КТД.
// - НРВПО чл. 42: пропорционално на признатия стаж по месеци; последният
//   месец при стаж над 1 месец е ПЪЛЕН при изработена поне половината от
//   РАБОТНИТЕ дни на месеца (работните дни — само през holidays.js).
// - чл. 176 КТ: отлагане (работодателят — до 10 раб. дни); ал. 2 — ползване
//   се осигурява до 6 месеца от края на годината; ал. 3 — иначе работникът
//   сам определя времето с 14-дневно писмено уведомление.
// - чл. 176а КТ: погасяване по давност — 2 години от края на годината, за
//   която се полага; при отложен по чл. 176, ал. 1 — 2 години от края на
//   годината, в която е отпаднала причината.

const { round2 } = require('./totals');
const holidays = require('./holidays');

// Основният минимум по чл. 155, ал. 4 КТ.
const MIN_ANNUAL_LEAVE_DAYS = 20;

const pad2 = (n) => (n < 10 ? '0' : '') + n;

function parseIso(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) throw new Error('Невалидна дата: ' + iso + ' (очаква се ГГГГ-ММ-ДД).');
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}
const lastDayOfMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();

// ---- В1. Пропорционален отпуск ----------------------------------------------

// Пропорцията по месеци (НРВПО чл. 42): годишният размер / 12 × месеци.
// ВАЖНО: наредбата НЕ съдържа правило за закръгляване на дробните дни —
// закръгляването (обичайно нагоре, в полза на работника) е практика на
// МТСП/вътрешни правила. Затова се връща ТОЧНАТА стойност + цялата част,
// а решението за дробта остава на потребителя (бележка в интерфейса).
//
// proRataFactor: пропорция за непълно работно време — прилага се само
// ИЗРИЧНО. При работно време поне половината от законоустановеното стажът
// се брои цял (чл. 355, ал. 2 КТ) и по официалното становище отпускът не
// се намалява автоматично; намаляване е допустимо, когато времето се
// признава пропорционално.
function proportionalLeave(opts) {
  const o = opts || {};
  const annual = Number(o.annualDays);
  const months = Number(o.months);
  if (!(annual > 0)) throw new Error('Въведете годишен размер на отпуска (поне ' + MIN_ANNUAL_LEAVE_DAYS + ' раб. дни).');
  if (!(months >= 0)) throw new Error('Невалиден брой месеци.');
  const factor = o.proRataFactor == null ? 1 : Number(o.proRataFactor);
  if (!(factor > 0 && factor <= 1)) throw new Error('Пропорцията за непълно време е между 0 и 1.');
  const exact = round2(annual * months / 12 * factor);
  return {
    exactDays: exact,
    wholeDays: Math.floor(exact),
    note: Number.isInteger(exact) ? '' :
      'Наредбата не урежда закръгляване на дробните дни — по практика се закръгля във вътрешните правила, обичайно в полза на работника.',
  };
}

// Месеците признат стаж по чл. 42 НРВПО за период от–до (вкл.): пълните
// календарни месеци + последният непълен месец се брои за ПЪЛЕН, ако са
// изработени поне половината от работните му дни.
function monthsOfService(opts) {
  const o = opts || {};
  const from = parseIso(o.from);
  const to = parseIso(o.to);
  if (o.from > o.to) throw new Error('Началото е след края на периода.');

  // Пълни месеци: от from до същото число на следващите месеци.
  let months = (to.y - from.y) * 12 + (to.m - from.m);
  if (to.d + 1 < from.d) months--; // не е стигнат пълният месечен кръг
  if (months < 0) months = 0;

  // Остатъкът след пълните месеци — в кой месец пада и колко РАБОТНИ дни
  // от него са изработени.
  const anchor = new Date(Date.UTC(from.y, from.m - 1 + months, Math.min(from.d, lastDayOfMonth(from.y, from.m + months))));
  const restFrom = anchor.getUTCFullYear() + '-' + pad2(anchor.getUTCMonth() + 1) + '-' + pad2(anchor.getUTCDate());
  if (restFrom > o.to) return months;

  const y = Number(o.to.slice(0, 4)), m = Number(o.to.slice(5, 7));
  const monthStart = o.to.slice(0, 7) + '-01';
  const monthEnd = o.to.slice(0, 7) + '-' + pad2(lastDayOfMonth(y, m));
  const workedInMonth = holidays.workingDaysBetween(monthStart > restFrom ? monthStart : restFrom, o.to);
  const totalInMonth = holidays.workingDaysBetween(monthStart, monthEnd);
  if (totalInMonth > 0 && workedInMonth >= totalInMonth / 2) months++;
  return months;
}

// Право на първо ползване (чл. 155, ал. 2): 4 месеца от постъпването при
// ПЪРВИ трудов стаж. Календарно: същото число 4 месеца напред.
function firstLeaveEligibleDate(startIso) {
  const s = parseIso(startIso);
  const t = new Date(Date.UTC(s.y, s.m - 1 + 4, Math.min(s.d, lastDayOfMonth(s.y, s.m + 4))));
  return t.getUTCFullYear() + '-' + pad2(t.getUTCMonth() + 1) + '-' + pad2(t.getUTCDate());
}

// ---- В2. Давност ------------------------------------------------------------

// Кога се погасява отпускът за дадена година (чл. 176а) и докога
// работодателят дължи осигурено ползване (чл. 176, ал. 2).
function leaveExpiry(opts) {
  const o = opts || {};
  const forYear = Number(o.forYear);
  if (!Number.isInteger(forYear) || forYear < 1990 || forYear > 2200) throw new Error('Невалидна година: ' + o.forYear);
  // При отложен по чл. 176, ал. 1 давността тече от края на годината, в
  // която е ОТПАДНАЛА причината (ал. 2 на чл. 176а).
  const baseYear = o.deferred && o.causeEndedYear ? Number(o.causeEndedYear) : forYear;
  return {
    expiresOn: (baseYear + 2) + '-12-31',
    mustUseBy: (forYear + 1) + '-06-30',
    notes: [
      'Работодателят е длъжен да осигури ползването до 6 месеца от края на годината, за която се полага (чл. 176, ал. 2 КТ).',
      'Не разреши ли в този срок, работникът има право САМ да определи времето с писмено уведомление поне 14 дни по-рано (чл. 176, ал. 3 КТ).',
      'По практиката на Съда на ЕС (Max-Planck) автоматичното погасяване е спорно, ако работодателят не е поканил изрично работника да ползва отпуска — третирайте датата като ориентир, не като присъда.',
    ],
  };
}

// Статус на остатъците по години към дадена дата: изтекъл / изтича скоро.
function leaveStatus(opts) {
  const o = opts || {};
  const asOf = String(o.asOf || '');
  parseIso(asOf);
  const asOfYear = Number(asOf.slice(0, 4));
  return (o.balances || []).map((b) => {
    const e = leaveExpiry({ forYear: b.year, deferred: b.deferred, causeEndedYear: b.causeEndedYear });
    const expired = e.expiresOn < asOf;
    return {
      year: Number(b.year),
      days: Number(b.days) || 0,
      expiresOn: e.expiresOn,
      mustUseBy: e.mustUseBy,
      expired,
      // „Скоро" = погасява се до края на ТЕКУЩАТА година — точно това е
      // списъкът „за планиране до декември".
      warnSoon: !expired && Number(e.expiresOn.slice(0, 4)) === asOfYear,
    };
  });
}

module.exports = {
  proportionalLeave,
  monthsOfService,
  firstLeaveEligibleDate,
  leaveExpiry,
  leaveStatus,
  MIN_ANNUAL_LEAVE_DAYS,
};
